/**
 * ProtectedRoute Component
 * Route guard that requires authentication
 */

import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import Loading from './ui/Loading'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: string[]
}

/**
 * ProtectedRoute wrapper
 * Redirects to login if user is not authenticated
 * Optionally checks for required roles
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking auth state
  if (isLoading) {
    // Detect Shopify OAuth redirect and show a more informative message
    const params = new URLSearchParams(location.search)
    const shopifyResult = params.get('shopify')
    const shopifyMessage = shopifyResult === 'success' || shopifyResult === 'reactivated'
      ? 'Completing Shopify connection… Please wait, do not navigate away.'
      : shopifyResult === 'error' || shopifyResult === 'shop_locked' || shopifyResult === 'org_mismatch' || shopifyResult === 'install_unverified'
        ? 'Returning from Shopify… Please wait.'
        : undefined

    return <Loading fullScreen message={shopifyMessage} />
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Save the location they were trying to access
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check role requirements if specified
  if (requiredRole && user && !requiredRole.includes(user.role)) {
    return (
      <div className="unauthorized-container">
        <h1>Access Denied</h1>
        <p>You do not have permission to access this page.</p>
        <p>Required role: {requiredRole.join(' or ')}</p>
      </div>
    )
  }

  // User is authenticated and has required role (if specified)
  return <>{children}</>
}

export default ProtectedRoute
